import { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Loader2, Receipt, Package, CheckCircle2, Clock, MessageSquare, Coins, Star, FileDown, ShieldAlert, CalendarClock } from "lucide-react";
import api from "../lib/apiClient";
import { toast } from "sonner";

const STATUS = {
  PENDING_PAYMENT: { label: "En attente de paiement", cls: "text-amber-400 bg-amber-500/10 border-amber-500/30" },
  PAID: { label: "Payée · en cours", cls: "text-sky-400 bg-sky-500/10 border-sky-500/30" },
  DELIVERED: { label: "Livrée · à valider", cls: "text-violet-400 bg-violet-500/10 border-violet-500/30" },
  COMPLETED: { label: "Terminée", cls: "text-emerald-400 bg-emerald-500/10 border-emerald-500/30" },
  DISPUTED: { label: "Litige en cours", cls: "text-red-400 bg-red-500/10 border-red-500/30" },
  CANCELLED: { label: "Annulée", cls: "text-zinc-400 bg-zinc-800 border-zinc-700" },
  REFUNDED: { label: "Remboursée", cls: "text-red-400 bg-red-500/10 border-red-500/30" },
};

export default function Orders() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [orders, setOrders] = useState(null);
  const [reviewFor, setReviewFor] = useState(null);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");

  const load = useCallback(() => api.get("/marketplace/orders").then((r) => setOrders(r.data.orders)).catch(() => setOrders([])), []);
  useEffect(() => { load(); }, [load]);

  // Retour de paiement Stripe → poll du statut
  useEffect(() => {
    const paid = params.get("paid");
    if (!paid) return;
    let tries = 0;
    const poll = async () => {
      tries += 1;
      try {
        const { data } = await api.get(`/marketplace/orders/${paid}/payment-status`);
        if (data.paid) { toast.success("Paiement confirmé — les fonds sont sécurisés."); load(); setParams({}); return; }
      } catch { /* keep polling */ }
      if (tries >= 8) { toast.info("Paiement en cours de traitement…"); load(); setParams({}); return; }
      setTimeout(poll, 2000);
    };
    poll();
  }, [params, load, setParams]);

  useEffect(() => {
    if (params.get("cancelled")) { toast.info("Paiement annulé."); setParams({}); }
  }, [params, setParams]);

  const deliver = async (id) => {
    try { await api.post(`/marketplace/orders/${id}/deliver`, { note: "" }); toast.success("Prestation marquée comme livrée."); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Échec"); }
  };
  const complete = async (o) => {
    try { await api.post(`/marketplace/orders/${o.id}/complete`); toast.success("Livraison validée — le professionnel a été payé."); setReviewFor(o.pro_id); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Échec"); }
  };
  const invoice = async (o) => {
    try {
      const res = await api.get(`/marketplace/orders/${o.id}/invoice`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = `facture-${o.invoice_number || o.id}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.response?.data?.detail || "Facture indisponible"); }
  };
  const openDispute = async (o) => {
    const reason = window.prompt("Décris le problème rencontré avec cette commande :");
    if (!reason || !reason.trim()) return;
    try { await api.post(`/marketplace/orders/${o.id}/dispute`, { reason }); toast.success("Litige ouvert — notre équipe va l'examiner."); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Échec"); }
  };
  const submitReview = async () => {
    try { await api.post(`/marketplace/professionals/${reviewFor}/reviews`, { rating, comment }); toast.success("Avis publié"); setReviewFor(null); setComment(""); }
    catch (e) { toast.error(e.response?.data?.detail || "Échec"); }
  };

  if (orders === null) return <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 text-amber-500 animate-spin" /></div>;

  return (
    <div className="space-y-5 max-w-3xl" data-testid="orders-page">
      <div>
        <p className="text-sm font-mono uppercase tracking-wider text-amber-500">Marché Cehnura</p>
        <h1 className="font-display text-3xl font-extrabold mt-1">Mes commandes</h1>
        <p className="text-zinc-500 mt-2">Devis acceptés, paiements sécurisés et suivi de tes prestations.</p>
      </div>

      {orders.length === 0 ? (
        <div className="text-center py-16 text-zinc-500 text-sm" data-testid="orders-empty">
          <Receipt className="w-10 h-10 mx-auto mb-3 text-zinc-700" />
          Aucune commande pour l'instant. Elles apparaissent quand un devis est accepté.
        </div>
      ) : (
        <div className="space-y-3">
          {orders.map((o) => {
            const st = STATUS[o.status] || STATUS.PENDING_PAYMENT;
            return (
              <div key={o.id} className="bg-[#18181E] border border-zinc-800 rounded-3xl p-5" data-testid={`order-${o.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <p className="font-semibold truncate">{o.title}</p>
                    <p className="text-xs text-zinc-500 mt-0.5">
                      {o.role === "pro" ? `Client : ${o.client_name}` : `Pro : ${o.pro_name}`}
                      {o.request_title ? ` · ${o.request_title}` : ""}
                    </p>
                  </div>
                  <span className={`text-[11px] px-2.5 py-1 rounded-full border font-medium ${st.cls}`} data-testid={`order-status-${o.id}`}>{st.label}</span>
                </div>

                <div className="flex flex-wrap items-center gap-4 mt-3 text-sm">
                  <span className="inline-flex items-center gap-1 text-zinc-200 font-semibold"><Coins className="w-4 h-4 text-amber-400" />{o.amount?.toFixed(2)} €</span>
                  {o.role === "pro" ? (
                    <span className="text-xs text-zinc-500">Tu reçois {o.pro_amount?.toFixed(2)} € (commission {(o.commission_rate * 100).toFixed(0)}% : {o.platform_fee?.toFixed(2)} €)</span>
                  ) : (
                    <span className="text-xs text-zinc-500">Dont commission plateforme {(o.commission_rate * 100).toFixed(0)}% ({o.platform_fee?.toFixed(2)} €)</span>
                  )}
                </div>

                {o.booking?.start && (
                  <p className="text-xs text-violet-300 mt-2 inline-flex items-center gap-1" data-testid={`order-booking-${o.id}`}>
                    <CalendarClock className="w-3.5 h-3.5" />Créneau réservé : {o.booking.start.slice(0, 16).replace("T", " ")}
                  </p>
                )}

                <div className="flex flex-wrap items-center gap-2 mt-4">
                  {o.conversation_id && (
                    <button onClick={() => navigate(`/messages/${o.conversation_id}`)} data-testid={`order-chat-${o.id}`}
                      className="text-xs px-3 h-8 rounded-lg border border-zinc-700 text-zinc-300 inline-flex items-center gap-1"><MessageSquare className="w-3 h-3" />Messagerie</button>
                  )}
                  {o.role === "pro" && o.status === "PAID" && (
                    <button onClick={() => deliver(o.id)} data-testid={`order-deliver-${o.id}`}
                      className="text-xs px-3 h-8 rounded-lg gold-gradient text-black font-semibold inline-flex items-center gap-1"><Package className="w-3 h-3" />Marquer comme livré</button>
                  )}
                  {o.role === "client" && (o.status === "DELIVERED" || o.status === "PAID") && (
                    <button onClick={() => complete(o)} data-testid={`order-complete-${o.id}`}
                      className="text-xs px-3 h-8 rounded-lg gold-gradient text-black font-semibold inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" />Valider & payer le pro</button>
                  )}
                  {!["PENDING_PAYMENT", "CANCELLED"].includes(o.status) && (
                    <button onClick={() => invoice(o)} data-testid={`order-invoice-${o.id}`}
                      className="text-xs px-3 h-8 rounded-lg border border-zinc-700 text-zinc-300 inline-flex items-center gap-1"><FileDown className="w-3 h-3" />Facture PDF</button>
                  )}
                  {o.role === "client" && ["PAID", "DELIVERED"].includes(o.status) && (
                    <button onClick={() => openDispute(o)} data-testid={`order-dispute-${o.id}`}
                      className="text-xs px-3 h-8 rounded-lg border border-red-500/40 text-red-400 inline-flex items-center gap-1"><ShieldAlert className="w-3 h-3" />Signaler un problème</button>
                  )}
                  {o.status === "PENDING_PAYMENT" && <span className="text-xs text-amber-400 inline-flex items-center gap-1"><Clock className="w-3 h-3" />Paiement à finaliser depuis la messagerie</span>}
                </div>

                {reviewFor === o.pro_id && o.role === "client" && (
                  <div className="mt-4 border-t border-zinc-800 pt-3" data-testid="order-review-panel">
                    <div className="flex items-center gap-1 mb-2">
                      {[1, 2, 3, 4, 5].map((s) => (
                        <button key={s} onClick={() => setRating(s)} data-testid={`order-review-star-${s}`}>
                          <Star className={`w-5 h-5 ${s <= rating ? "text-amber-400 fill-amber-400" : "text-zinc-600"}`} />
                        </button>
                      ))}
                    </div>
                    <textarea value={comment} onChange={(e) => setComment(e.target.value)} data-testid="order-review-comment" rows={2} placeholder="Ton avis sur ce professionnel" className="w-full px-3 py-2 bg-zinc-900 border border-zinc-800 rounded-xl text-sm text-zinc-100 resize-none" />
                    <button onClick={submitReview} data-testid="order-review-submit" className="mt-2 h-9 px-4 rounded-lg gold-gradient text-black text-sm font-semibold">Publier l'avis</button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
